import { useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { notificationQueries } from '../api/notification';
import { NotificationCategory } from '../constants/types/notification';
import useNotificationApiInstance from './useNotificationApiInstance';

import type { Notification } from '../constants/types/notification';

export type NotificationTabKey = 'all' | 'resource' | 'platform_notice';

interface UseNotificationFeedOptions {
  /** 단일 카테고리 필터. 탭별 독립 조회에 사용. 미지정 시 전체. */
  category?: NotificationCategory;
  enabled?: boolean;
  /** 페이지당 요청 개수. 기본 10. */
  limit?: number;
  /** 활성 탭. 지정하면 tab에 맞는 category가 적용된다. */
  tab?: NotificationTabKey;
}

/**
 * 탭별 서버 카테고리 필터 매핑.
 * @description
 * - 'all': 필터 없음 (전체)
 * - 'resource': category=operation
 * - 'platform_notice': category=platform_notice
 */
const TAB_CATEGORY: Record<NotificationTabKey, NotificationCategory | undefined> = {
  all: undefined,
  resource: NotificationCategory.OPERATION,
  platform_notice: NotificationCategory.PLATFORM_NOTICE,
};

/**
 * Notification feed (cursor pagination) 훅.
 * @description
 * - 10개씩 요청, maxPages 100으로 1000개 cap.
 * - 조회 relevance(mine)와 global 알림(새소식) 포함 여부는 서버 기본값을 따른다
 *   (notificationRelevance=mine / includeGlobalNotifications=true). 탭 구분은 category 만으로 충분하다.
 * - meta endpoint 가 준비되지 않으면 자동 비활성.
 * - tab → category 매핑과 페이지 flatten 결과(visible)를 함께 제공한다.
 */
const useNotificationFeed = (options: UseNotificationFeedOptions = {}) => {
  const { limit = 10, enabled = true, category, tab = 'all' } = options;
  const { instance, endpoint } = useNotificationApiInstance();
  const resolvedCategory = category ?? TAB_CATEGORY[tab];

  const queryResult = useInfiniteQuery({
    ...notificationQueries.feed(instance, limit, resolvedCategory),
    enabled: enabled && Boolean(endpoint),
  });

  const visible = useMemo<Notification[]>(
    () => queryResult.data?.pages.flatMap(page => page.data) ?? [],
    [queryResult.data],
  );

  return {
    ...queryResult,
    hasNextPage: Boolean(queryResult.hasNextPage),
    visible,
  };
};

export default useNotificationFeed;
